import { React, useCallback, useEffect, useMemo, useState } from "react";
import MyAvatar from "@components/admin/MyAvatar";
import { ArrowBack, ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import {
  IconButton,
  Typography,
  TextField,
  MenuItem,
  Switch,
  Button,
  Select,
  OutlinedInput,
  Chip,
  Checkbox,
  ListItemText,
  Divider,
  InputAdornment,
} from "@mui/material";

import locationOptions from "../../../constants/locationOptions";
import { axios } from "@apiClient";
import { toast } from "react-toastify";

export default function CommunityEngagementTab({
  id,
  setId,
  community,
  tabs,
  communityRefresh,
}) {
  const DEFAULT_INTERVALS = useMemo(
    () => ["60 Days", "30 Days", "15 Days", "5 Days", "3 Days", "1 Day"],
    [],
  );

  const [intervals, setIntervals] = useState(DEFAULT_INTERVALS);

  // Controlled resurfacing settings
  const [enabled, setEnabled] = useState(false);
  const [allowEventPosts, setAllowEventPosts] = useState(false);
  const [maxDifferent, setMaxDifferent] = useState(1);
  const [maxPerEvent, setMaxPerEvent] = useState(1);
  const [maxTotal, setMaxTotal] = useState(1);
  const [cooldownMinutes, setCooldownMinutes] = useState(1);
  const [reachRadius, setReachRadius] = useState(1);
  const [ageRelevance, setAgeRelevance] = useState(false);
  const [excludeBlockedTopics, setExcludeBlockedTopics] = useState(false);
  const [locationScopeSelected, setLocationScopeSelected] = useState([]);
  const [communityGroups, setCommunityGroups] = useState([]);
  const [disabledGroupsEvent, setDisabledGroupsEvent] = useState([]);

  const [intervalInput, setIntervalInput] = useState("");
  const [showIntervalInput, setShowIntervalInput] = useState(false);

  const handleAddInterval = (value) => {
    const trimmed = (value ?? intervalInput).trim();
    if (!trimmed) {
      setShowIntervalInput(false);
      return;
    }
    const numericValue = parseInt(trimmed.replace(/\D/g, ""), 10);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 9999) {
      return;
    }

    const formatted = trimmed.endsWith("Days") ? trimmed : `${trimmed} Days`;
    setIntervals((prev) => {
      if (prev.includes(formatted)) {
        return prev;
      }
      return [...prev, formatted];
    });
    setIntervalInput("");
    setShowIntervalInput(false);
  };

  const handleRemove = (itemToRemove) => {
    setIntervals((prev) => prev.filter((item) => item !== itemToRemove));
  };

  const [resetKey, setResetKey] = useState(1);
  const [initialSettings, setInitialSettings] = useState(null);

  const loadEngagementSettings = useCallback(async () => {
    if (!community?.id) return;
    try {
      const response = await axios.get(`/engagement/${community.id}`);
      if (response.data) {
        const r = response.data;

        const normalizedIntervals = (r.intervals ?? DEFAULT_INTERVALS).map(
          (interval) =>
            typeof interval === "number" ? `${interval} Days` : interval,
        );
        const normalizedDisabledGroups = (r.disabledGroupsEvent ?? []).map(
          (g) => (typeof g === "object" ? g.id : g),
        );

        const normalizeLocationScope = (scope) => {
          if (!scope || scope.length === 0) return [];
          return scope.map((item) =>
            typeof item === "string" ? { label: item, miles: 0 } : item,
          );
        };

        const settings = {
          enabled: r.enabled ?? true,
          maxDifferent: r.maxDifferentPostsPer24Hours ?? 1,
          maxPerEvent: r.maxResurfacesPerEvent24Hours ?? 1,
          maxTotal: r.maxTotalResurfacedPerDay ?? 1,
          cooldownMinutes: r.cooldownMinutes ?? 1,
          intervals: normalizedIntervals,
          reachRadius: r.reachRadius ?? 1,
          ageRelevance: r.ageRelevance ?? false,
          excludeBlockedTopics: r.excludeBlockedTopics ?? false,
          locationScope: normalizeLocationScope(r.locationScope),
          disabledGroupsEvent: normalizedDisabledGroups,
        };

        // ✅ Set state
        setEnabled(settings.enabled);
        setMaxDifferent(settings.maxDifferent);
        setMaxPerEvent(settings.maxPerEvent);
        setMaxTotal(settings.maxTotal);
        setCooldownMinutes(settings.cooldownMinutes);
        setIntervals(settings.intervals);
        setReachRadius(settings.reachRadius);
        setAgeRelevance(settings.ageRelevance);
        setExcludeBlockedTopics(settings.excludeBlockedTopics);
        setLocationScopeSelected(settings.locationScope);
        setDisabledGroupsEvent(settings.disabledGroupsEvent);

        // ✅ Snapshot for change detection — normalized same as state
        setInitialSettings(settings);
      }
    } catch (err) {
      console.log("No engagement settings found, using defaults");

      // ✅ FIX — set initialSettings to defaults so hasChanges works correctly
      const defaultSettings = {
        enabled: false,
        maxDifferent: 1,
        maxPerEvent: 1,
        maxTotal: 1,
        cooldownMinutes: 1,
        intervals: DEFAULT_INTERVALS,
        reachRadius: 1,
        ageRelevance: false,
        excludeBlockedTopics: false,
        locationScope: [],
        disabledGroupsEvent: [],
      };
      setInitialSettings(defaultSettings);
    }
  }, [community?.id, DEFAULT_INTERVALS]);

  // initialize from engagement settings if available
  useEffect(() => {
    if (!community?.id) return;
    loadEngagementSettings();
  }, [community?.id, id, loadEngagementSettings]);

  // Fetch community groups
  useEffect(() => {
    const fetchGroups = async () => {
      if (!community?.id) return;
      try {
        // ✅ Admin endpoint — returns ALL groups (public + private)
        const response = await axios.get(
          `/admin/group/community-groups/${community.id}`,
        );
        if (response.data?.groups) {
          setCommunityGroups(response.data.groups);
        }
      } catch (err) {
        console.log("No groups found for community");
      }
    };
    fetchGroups();
  }, [community?.id]);
  return (
    <div className="grow py-4 px-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 items-center">
          <IconButton onClick={() => setId(null)}>
            <ArrowBack />
          </IconButton>

          <MyAvatar
            name={community.name}
            subtext={`${community?.users?.length} users`}
            src={community?.icon?.currBucketUrl}
            sx={{ height: 50, width: 50 }}
          />
        </div>

        {tabs}
      </div>

      {/* Body */}
      <div key={resetKey} className="mt-6 bg-white p-8 rounded-xl">
        {/* Post Type */}
        <div className="grid grid-cols-3 items-center mb-8 ">
          <Typography className="font-medium whitespace-nowrap justify-self-start">
            Post Type:
          </Typography>
          <TextField
            select
            size="small"
            value={"events"}
            className="w-80 justify-self-center "
          >
            <MenuItem value="events">Events</MenuItem>
            <MenuItem value="posts">Standard Post</MenuItem>
          </TextField>
        </div>
        <Divider />

        {/* Frequency Limits */}
        <div className="pt-6">
          <Section title="Frequency Limits">
            <NumberInput
              label="Max Unique Posts per Day:"
              description="Maximum number of resurfaced event posts a person can receive per day"
              value={maxDifferent}
              onChange={(v) => setMaxDifferent(v)}
            />
            <NumberInput
              label="Max Resurfaces per Post per Day"
              description="Maximum number of times the same event post can be delivered across the community per day."
              value={maxPerEvent}
              onChange={(v) => setMaxPerEvent(v)}
            />
            <NumberInput
              label="Total Resurfaced Posts per Day"
              description="Maximum number of times the same event can be resurfaced to a person per day."
              value={maxTotal}
              onChange={(v) => setMaxTotal(v)}
            />
            <NumberInput
              label="Cooldown period (minutes)"
              description="Minimum number of time the same event can be resurfaced to a person per day."
              value={cooldownMinutes}
              onChange={(v) => setCooldownMinutes(v)}
            />
          </Section>
        </div>
        <Divider />

        {/* Eligibility Timing */}
        {/* Eligibility Timing */}
        <div className="py-6">
          <div className="flex justify-between items-start gap-4">
            <div>
              <Typography
                className="font-semibold mb-1"
                sx={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Eligibility Timing
              </Typography>
              <Typography className="text-sm text-gray-500 whitespace-nowrap">
                Day before event when resurfacing activates
              </Typography>
            </div>

            {/* Expanded grid width to perfectly accommodate the extra inner horizontal padding */}
            <div className="w-[390px] grid grid-cols-4 gap-x-2 gap-y-3 font-medium shrink-0">
              {intervals.map((item) => (
                <span
                  key={item}
                  className="px-3.5 py-1.5 bg-green-50/60 text-emerald-500 border border-emerald-100 rounded-full text-xs flex items-center justify-between gap-1 h-8 box-border hover:bg-green-50 transition-colors"
                >
                  <span className="leading-none font-semibold whitespace-nowrap">
                    {item}
                  </span>
                  <button
                    onClick={() => handleRemove(item)}
                    className="text-emerald-400 hover:text-emerald-600 font-bold text-[10px] leading-none transition-colors shrink-0"
                  >
                    ✕
                  </button>
                </span>
              ))}

              {/* Permanent matching input chip wrapper with matching padding */}
              <span className="px-3.5 py-1.5 bg-green-50/40 border border-emerald-100 rounded-full text-xs flex items-center justify-between gap-1 h-8 box-border">
                <input
                  className="w-full outline-none text-xs text-emerald-600 placeholder-emerald-300/80 bg-transparent leading-none font-semibold"
                  placeholder="Type.."
                  value={intervalInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setIntervalInput(val);
                      return;
                    }
                    const num = parseInt(val, 10);
                    if (/^\d+$/.test(val) && num >= 0 && num <= 9999) {
                      setIntervalInput(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const trimmed = intervalInput.trim();
                      if (!trimmed) return;

                      const numericValue = parseInt(trimmed.replace(/\D/g, ""), 10);
                      if (isNaN(numericValue) || numericValue < 0 || numericValue > 9999) {
                        return;
                      }

                      const formatted = trimmed.endsWith("Days") || trimmed.endsWith("Day")
                        ? trimmed
                        : `${trimmed} Days`;
                      setIntervals((prev) => {
                        if (prev.includes(formatted)) {
                          return prev;
                        }
                        return [...prev, formatted];
                      });
                      setIntervalInput("");
                    }
                  }}
                />
                <button
                  onClick={() => setIntervalInput("")}
                  className="text-emerald-400 hover:text-emerald-600 font-bold text-[10px] leading-none transition-colors shrink-0"
                >
                  ✕
                </button>
              </span>
            </div>
          </div>
        </div>
        <Divider />

        {/* Targeting Rules */}
        <div className="pt-6">
          <Section title="Targeting Rules">
            <div className="mb-5">
              <div className="flex justify-between items-center gap-4">
                <Typography className="text-sm whitespace-nowrap">
                  Reach Radius (miles):
                </Typography>
                <ReachRadiusInput value={reachRadius} onChange={setReachRadius} />
              </div>
            </div>

            <SwitchRow
              label="Age Relevance:"
              checked={ageRelevance}
              onChange={(v) => setAgeRelevance(v)}
            />
            <SwitchRow
              label="Exclude Blocked Topics:"
              checked={excludeBlockedTopics}
              onChange={(v) => setExcludeBlockedTopics(v)}
            />
          </Section>
        </div>
        <Divider />

        {/* General Controls */}
        <div className="pt-6">
          <Section title="General Controls">
            <LocationScope
              selected={locationScopeSelected}
              onChange={(v) => setLocationScopeSelected(v)}
            />
            <SwitchRow
              label="Enable Resurfacing:"
              checked={enabled}
              onChange={(v) => setEnabled(v)}
            />
            <DisabledGroupsSelector
              groups={communityGroups}
              selected={disabledGroupsEvent}
              onChange={(v) => setDisabledGroupsEvent(v)}
            />
          </Section>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 mt-10">
          <Button
            variant="outlined"
            sx={{ borderColor: "#10b981", color: "#10b981" }}
            onClick={() => {
              // reset to default values
              setResetKey((k) => k + 1);
              setEnabled(false);
              setAllowEventPosts(false);
              setMaxDifferent(1);
              setMaxPerEvent(1);
              setMaxTotal(1);
              setCooldownMinutes(1);
              setReachRadius(1);
              setAgeRelevance(false);
              setExcludeBlockedTopics(false);
              setLocationScopeSelected([]);
              setDisabledGroupsEvent([]);
              setIntervals(DEFAULT_INTERVALS);
            }}
          >
            Reset Changes
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#10b981",
              "&:hover": { backgroundColor: "#059669" },
            }}
            onClick={async () => {
              if (!community) return;

              // Check if there are changes
              const hasChanges = !(
                initialSettings &&
                enabled === initialSettings.enabled &&
                maxDifferent === initialSettings.maxDifferent &&
                maxPerEvent === initialSettings.maxPerEvent &&
                maxTotal === initialSettings.maxTotal &&
                cooldownMinutes === initialSettings.cooldownMinutes &&
                JSON.stringify(intervals) ===
                JSON.stringify(initialSettings.intervals) &&
                reachRadius === initialSettings.reachRadius &&
                ageRelevance === initialSettings.ageRelevance &&
                excludeBlockedTopics === initialSettings.excludeBlockedTopics &&
                JSON.stringify(locationScopeSelected) ===
                JSON.stringify(initialSettings.locationScope) &&
                JSON.stringify(disabledGroupsEvent) ===
                JSON.stringify(initialSettings.disabledGroupsEvent)
              );

              if (!hasChanges) {
                toast.info("No changes to save");
                return;
              }

              // Validation
              if (
                maxDifferent === 1 ||
                maxPerEvent === 1 ||
                maxTotal === 1 ||
                cooldownMinutes === 1 ||
                intervals.length === 1 ||
                locationScopeSelected.length === 1
              ) {
                toast.error("Please fill all fields...");
                return;
              }

              if (
                reachRadius < REACH_RADIUS_MIN ||
                reachRadius > REACH_RADIUS_MAX
              ) {
                toast.error(
                  `Reach Radius must be between ${REACH_RADIUS_MIN} and ${REACH_RADIUS_MAX} miles`,
                );
                return;
              }

              const payload = {
                communityId: community.id,
                enabled,
                maxDifferentPostsPer24Hours: Number(maxDifferent),
                maxResurfacesPerEvent24Hours: Number(maxPerEvent),
                maxTotalResurfacedPerDay: Number(maxTotal),
                cooldownMinutes: Number(cooldownMinutes),
                intervals: intervals.map((interval) => {
                  const num = parseInt(interval, 10);
                  return isNaN(num) ? 0 : num;
                }),
                reachRadius,
                ageRelevance,
                excludeBlockedTopics,
                locationScope: locationScopeSelected,
                // ✅ FIX: ensure plain string IDs, not objects
                disabledGroupsEvent: disabledGroupsEvent.map((g) =>
                  typeof g === "object" ? g.id : g,
                ),
              };
              try {
                await axios.post("/engagement/create-or-update", payload);
                toast.success("Resurfacing settings saved");

                // ✅ Immediately update initialSettings to current state
                // so next click correctly detects no changes
                setInitialSettings({
                  enabled,
                  maxDifferent,
                  maxPerEvent,
                  maxTotal,
                  cooldownMinutes,
                  intervals, // already normalized strings
                  reachRadius,
                  ageRelevance,
                  excludeBlockedTopics,
                  locationScope: locationScopeSelected,
                  disabledGroupsEvent: disabledGroupsEvent.map((g) =>
                    typeof g === "object" ? g.id : g,
                  ),
                });

                await loadEngagementSettings(); // still reload from server to confirm
                communityRefresh?.();
              } catch (err) {
                console.error("Save error:", err);
                toast.error(
                  err.response?.data?.message || "Failed to save settings",
                );
                console.error("Full error:", err.response);
              }
            }}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reusable Components ---------------- */

const Section = ({ title, children }) => (
  <div className="mb-6">
    <Typography
      className="font-semibold mb-5"
      sx={{ textDecoration: "underline", fontWeight: 600 }}
    >
      {title}
    </Typography>
    {/* ✅ FIX: left-aligned, no mx-auto */}
    <div className="space-y-6">{children}</div>
  </div>
);

// Custom up/down arrows rendered right beside the number, replacing the
// browser's native spinner which sticks to the outer edge of the field.
const SpinButtons = ({ onIncrement, onDecrement }) => (
  <div className="flex flex-col justify-center -my-2 -space-y-1.5">
    <button
      type="button"
      tabIndex={-1}
      onClick={onIncrement}
      className="flex items-center justify-center h-3 w-3.5 leading-none text-gray-400 hover:text-gray-600"
    >
      <ArrowDropUp sx={{ fontSize: 18 }} />
    </button>
    <button
      type="button"
      tabIndex={-1}
      onClick={onDecrement}
      className="flex items-center justify-center h-3 w-3.5 leading-none text-gray-400 hover:text-gray-600"
    >
      <ArrowDropDown sx={{ fontSize: 18 }} />
    </button>
  </div>
);

const numberInputNoNativeSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& .MuiOutlinedInput-root": { paddingRight: "8px", justifyContent: "center" },
  "& .MuiOutlinedInput-input": { flexGrow: 0 },
  "& .MuiInputAdornment-root": { marginLeft: "2px" },
};

// Updated NumberInput to support a subtitle/description
const NumberInput = ({ label, description, value, onChange }) => {
  const [inputVal, setInputVal] = useState(value ?? 1);

  useEffect(() => {
    setInputVal(value ?? 1);
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    if (val.startsWith("-")) return;
    if (val === "") {
      setInputVal("");
      onChange(1);
      return;
    }

    const num = Number(val);
    if (Number.isNaN(num)) return;

    // Cap at 9999
    if (num > 9999) return;

    // ✅ FIX: strip leading zeros so "05" becomes "5"
    const stripped = String(Math.max(1, num));
    setInputVal(stripped);
    onChange(Math.max(1, num));
  };

  const handleFocus = () => {
    if (Number(inputVal) === 1) setInputVal(""); // ✅ FIX: Number() handles both 1 and "1"
  };

  const handleBlur = () => {
    // If user cleared the field, restore to 1
    if (inputVal === "") {
      setInputVal(1);
      onChange(1);
    }
  };

  const step = (delta) => {
    const next = Math.min(9999, Math.max(1, (Number(inputVal) || 0) + delta));
    setInputVal(String(next));
    onChange(next);
  };

  return (
    <div className="flex justify-between items-start gap-4">
      <div className="flex flex-col">
        <Typography className="text-sm font-medium whitespace-nowrap">
          {label}
        </Typography>
        {description && (
          <Typography className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">
            {description}
          </Typography>
        )}
      </div>
      <TextField
        type="number"
        size="small"
        value={inputVal}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        inputProps={{
          min: 1,
          style: {
            textAlign: "center",
            fontWeight: 600,
            color: "#6b7280",
            width: "3ch",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SpinButtons
                onIncrement={() => step(1)}
                onDecrement={() => step(-1)}
              />
            </InputAdornment>
          ),
        }}
        sx={numberInputNoNativeSpinnerSx}
        className="w-24 shrink-0"
      />
    </div>
  );
};
const REACH_RADIUS_MIN = 0.5;
const REACH_RADIUS_MAX = 25000;

// Reach Radius accepts decimal miles, clamped to [0.5, 25000]
const ReachRadiusInput = ({ value, onChange }) => {
  const [inputVal, setInputVal] = useState(String(value ?? REACH_RADIUS_MIN));

  useEffect(() => {
    setInputVal(String(value ?? REACH_RADIUS_MIN));
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    // allow empty string and partial decimal input while typing (e.g. "12.")
    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
    setInputVal(val);

    if (val === "" || val === ".") return;
    const num = Number(val);
    if (Number.isNaN(num)) return;
    onChange(num);
  };

  const handleBlur = () => {
    let num = Number(inputVal);
    if (inputVal === "" || Number.isNaN(num)) num = REACH_RADIUS_MIN;
    num = Math.min(REACH_RADIUS_MAX, Math.max(REACH_RADIUS_MIN, num));
    setInputVal(String(num));
    onChange(num);
  };

  const step = (delta) => {
    const next = Math.min(
      REACH_RADIUS_MAX,
      Math.max(REACH_RADIUS_MIN, (Number(inputVal) || 0) + delta),
    );
    setInputVal(String(next));
    onChange(next);
  };

  return (
    <TextField
      type="number"
      size="small"
      value={inputVal}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{
        min: REACH_RADIUS_MIN,
        max: REACH_RADIUS_MAX,
        step: 0.5,
        style: {
          textAlign: "center",
          fontWeight: 600,
          color: "#6b7280",
          width: "5ch",
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <SpinButtons
              onIncrement={() => step(0.5)}
              onDecrement={() => step(-0.5)}
            />
          </InputAdornment>
        ),
      }}
      sx={numberInputNoNativeSpinnerSx}
      className="w-24 shrink-0"
    />
  );
};

const SwitchRow = ({ label, checked = false, onChange = () => { } }) => (
  <div className="flex justify-between items-center">
    <Typography className="text-sm whitespace-nowrap">{label}</Typography>
    <div className="flex items-center gap-3">
      <Typography
        className={`text-sm font-semibold ${checked ? "text-gray-400" : "text-gray-500"}`}
      >
        OFF
      </Typography>
      <Switch
        checked={checked}
        onChange={(_, v) => onChange(v)}
        sx={{
          width: 74, // Increased from 62px to make it noticeably wider
          height: 28,
          padding: 0,
          display: "flex",
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "4px",
            transitionDuration: "200ms",
            color: "#fff",
            "&.Mui-checked": {
              transform: "translateX(46px)", // Updated to match the wider track length
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: "#50cd9e",
                opacity: 1,
                border: 0,
              },
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 20,
            height: 20,
            boxShadow: "none",
          },
          "& .MuiSwitch-track": {
            borderRadius: 28 / 2,
            backgroundColor: "#d1d5db",
            opacity: 1,
            transition: "background-color 200ms",
          },
        }}
      />
      <Typography
        className={`text-sm font-semibold ${checked ? "text-gray-500" : "text-gray-400"}`}
      >
        ON
      </Typography>
    </div>
  </div>
);

const LOCATION_MILES_MIN = 0.5;
const LOCATION_MILES_MAX = 9999;

// Miles input for a single location scope row — decimal-friendly, clamped to [0.5, 9999]
const ScopeMilesInput = ({ value, onChange }) => {
  const [inputVal, setInputVal] = useState(String(value ?? LOCATION_MILES_MIN));

  useEffect(() => {
    setInputVal(String(value ?? LOCATION_MILES_MIN));
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
    setInputVal(val);
    if (val === "" || val === ".") return;
    const num = Number(val);
    if (Number.isNaN(num)) return;
    onChange(num);
  };

  const handleBlur = () => {
    let num = Number(inputVal);
    if (inputVal === "" || Number.isNaN(num)) num = LOCATION_MILES_MIN;
    num = Math.min(LOCATION_MILES_MAX, Math.max(LOCATION_MILES_MIN, num));
    setInputVal(String(num));
    onChange(num);
  };

  const step = (delta) => {
    const next = Math.min(
      LOCATION_MILES_MAX,
      Math.max(LOCATION_MILES_MIN, (Number(inputVal) || 0) + delta),
    );
    setInputVal(String(next));
    onChange(next);
  };

  return (
    <TextField
      type="number"
      size="small"
      value={inputVal}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{
        min: LOCATION_MILES_MIN,
        max: LOCATION_MILES_MAX,
        step: 0.5,
        style: {
          textAlign: "center",
          fontWeight: 600,
          color: "#6b7280",
          width: "5ch",
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <SpinButtons onIncrement={() => step(0.5)} onDecrement={() => step(-0.5)} />
          </InputAdornment>
        ),
      }}
      sx={numberInputNoNativeSpinnerSx}
      className="w-24 shrink-0"
    />
  );
};

const LocationScope = ({ selected = [], onChange = () => { } }) => {
  const [options, setOptions] = useState(() => {
    const customLabels = selected
      .map((s) => s.label)
      .filter((label) => !locationOptions.includes(label));
    return [...locationOptions, ...customLabels];
  });
  const [milesMap, setMilesMap] = useState(() => {
    const map = {};
    selected.forEach((s) => {
      map[s.label] = s.miles;
    });
    return map;
  });
  const [customName, setCustomName] = useState("");
  const [customValue, setCustomValue] = useState("");
  const [error, setError] = useState("");

  // Keep options/miles in sync if settings are (re)loaded from the server
  useEffect(() => {
    setOptions((prev) => {
      const missing = selected
        .map((s) => s.label)
        .filter((label) => !prev.includes(label));
      return missing.length ? [...prev, ...missing] : prev;
    });
    setMilesMap((prev) => {
      const next = { ...prev };
      selected.forEach((s) => {
        next[s.label] = s.miles;
      });
      return next;
    });
  }, [selected]);

  const selectedLabels = selected.map((s) => s.label);

  const handleChange = (event) => {
    const newLabels = event.target.value.filter(
      (v) => v !== undefined && v !== null && v !== "",
    );
    onChange(
      newLabels.map((label) => ({
        label,
        miles: milesMap[label] ?? LOCATION_MILES_MIN,
      })),
    );
  };

  const handleMilesChange = (label, value) => {
    setMilesMap((prev) => ({ ...prev, [label]: value }));
    if (selectedLabels.includes(label)) {
      onChange(
        selected.map((s) => (s.label === label ? { ...s, miles: value } : s)),
      );
    }
  };

  const handleAddCustom = () => {
    const trimmedName = customName.trim();
    const trimmedValue = customValue.trim();
    if (!trimmedName || !trimmedValue) {
      setError("*Enter scope name and value.");
      return;
    }
    const num = Number(trimmedValue);
    if (
      Number.isNaN(num) ||
      num < LOCATION_MILES_MIN ||
      num > LOCATION_MILES_MAX
    ) {
      setError(
        `*Value must be between ${LOCATION_MILES_MIN} and ${LOCATION_MILES_MAX} miles.`,
      );
      return;
    }
    setError("");
    if (!options.includes(trimmedName)) {
      setOptions((prev) => [trimmedName, ...prev]);
    }
    setMilesMap((prev) => ({ ...prev, [trimmedName]: num }));
    onChange([
      ...selected.filter((s) => s.label !== trimmedName),
      { label: trimmedName, miles: num },
    ]);
    setCustomName("");
    setCustomValue("");
  };

  const handleRemoveOption = (optionToRemove) => {
    setOptions((prev) => prev.filter((opt) => opt !== optionToRemove));
    onChange(selected.filter((sel) => sel.label !== optionToRemove));
  };

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <Typography className="text-sm whitespace-nowrap">Location Scope:</Typography>

        <Select
          multiple
          displayEmpty
          value={selectedLabels}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={(vals) => {
            if (!vals || vals.length === 0) {
              return (
                <Typography sx={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                  Add location scope
                </Typography>
              );
            }
            return (
              <div className="flex flex-wrap gap-1">
                {vals.map((label) => (
                  <Chip
                    key={label}
                    label={`${label} (${milesMap[label] ?? LOCATION_MILES_MIN} miles)`}
                    size="small"
                  />
                ))}
              </div>
            );
          }}
          className="w-80"
          sx={{
            borderRadius: "4px",
            minHeight: "40px",
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              paddingTop: "8px",
              paddingBottom: "8px",
              fontSize: "0.875rem",
            },
            "& .MuiSelect-icon": {
              top: "calc(50% - 0.5em)",
            },
          }}
          MenuProps={{
            PaperProps: {
              style: { maxHeight: 320, overflowY: "auto" },
            },
            disableAutoFocusItem: true,
          }}
        >
          {/* Custom scope + value row pinned at top */}
          <div
            className="px-3 py-2 sticky top-0 bg-white z-10"
            onKeyDown={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
          >
            <div className="flex gap-2 items-center">
              <input
                className="border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:border-emerald-500"
                placeholder="Add custom scope..."
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  if (e.key === "Enter") handleAddCustom();
                }}
              />
              <input
                className="border border-gray-300 rounded px-2 py-1 text-sm w-28 shrink-0 focus:outline-none focus:border-emerald-500"
                placeholder="Value (miles)"
                value={customValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^\d*\.?\d*$/.test(val)) setCustomValue(val);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  if (e.key === "Enter") handleAddCustom();
                }}
              />
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAddCustom();
                }}
                className="text-emerald-500 font-semibold text-sm hover:text-emerald-600 whitespace-nowrap shrink-0"
              >
                + Add
              </button>
            </div>
            {error && (
              <Typography className="text-xs text-red-500 mt-1">{error}</Typography>
            )}
          </div>

          {options.map((name) => (
            <MenuItem key={name} value={name}>
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center">
                  <Checkbox checked={selectedLabels.includes(name)} />
                  <ListItemText primary={name} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                </div>
                <div
                  className="flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <ScopeMilesInput
                    value={milesMap[name] ?? LOCATION_MILES_MIN}
                    onChange={(v) => handleMilesChange(name, v)}
                  />
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveOption(name);
                    }}
                    className="text-red-500 hover:text-red-700 text-sm font-bold ml-1"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );
};
const DisabledGroupsSelector = ({
  groups = [],
  selected = [],
  onChange = () => { },
}) => {
  const handleDeselect = (groupId) => {
    onChange(selected.filter((id) => id !== groupId));
  };

  return (
    <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
      <Typography className="text-sm">Restrict From Adding Event:</Typography>

      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={(event) => onChange(event.target.value)}
        input={<OutlinedInput />}
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return (
              <Typography sx={{ color: "#9ca3af", fontSize: "0.875rem" }}>
                Select groups...
              </Typography>
            );
          }
          return (
            <div className="flex flex-wrap gap-1 max-w-full">
              {selected.map((groupId) => {
                const group = groups.find((g) => g.id === groupId);
                return (
                  <Chip
                    key={groupId}
                    label={group?.name ?? groupId}
                    size="small"
                    sx={{ maxWidth: "100%" }}
                  />
                );
              })}
            </div>
          );
        }}
        className="w-80 max-w-full"
        sx={{
          borderRadius: "4px",
          minHeight: "40px",
          "& .MuiSelect-select": {
            paddingTop: "8px",
            paddingBottom: "8px",
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            overflow: "hidden"
          }
        }}
        MenuProps={{
          anchorOrigin: { vertical: "top", horizontal: "left" },
          transformOrigin: { vertical: "bottom", horizontal: "left" },
          PaperProps: {
            style: { maxHeight: 175, overflowY: "auto" },
          },
          disableAutoFocusItem: true,
        }}
      >
        {groups && groups.length > 0 ? (
          groups.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center">
                  <Checkbox checked={selected.includes(group.id)} />
                  <ListItemText primary={group.name} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                </div>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeselect(group.id);
                  }}
                  className="text-red-500 hover:text-red-700 text-sm font-bold ml-2"
                >
                  ✕
                </button>
              </div>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography className="text-sm text-gray-500">
              No groups available
            </Typography>
          </MenuItem>
        )}
      </Select>
    </div>
  );
};